import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Collapse, TextField, Typography } from '@mui/material';
import { getIdeaOptions, submitIdea } from '../../services/workstreamIdeaService';

// TR palette and shadows from DESIGN.md. Orange is used once: the progress bar (and keyboard focus).
const C = {
  green: '#1A2E22', greenLight: '#2C4A3A', greenDark: '#0F1A14', orange: '#D74B00',
  floor: '#F8F9FA', paper: '#FFFFFF', ink: '#1A1A1A', ink2: '#3D4540', muted: '#666666',
  line: '#E3E8E5', hair: 'rgba(0,0,0,0.06)', field: '#CBD3CE', onGreen: '#E4ECE6', onGreenMuted: '#B9CEC0',
};
const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';
const SHADOW = '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)';
const FOCUS = { outline: `3px solid ${C.orange}`, outlineOffset: '2px' };
const REDUCED_MOTION = { '@media (prefers-reduced-motion: reduce)': { transition: 'none' } };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// limits.evidenceLinks and limits.adoWorkItems are counts, not character limits.
const TEXT_LIMIT_FIELDS = ['title', 'problem', 'proposedSolution', 'affectedService', 'submitterName', 'submitterEmail'];

const EMPTY = {
  title: '', category: '', affectedService: '', impactType: '', problem: '', proposedSolution: '',
  evidenceLinks: '', adoWorkItems: '', submitterName: '', submitterEmail: '', website: '',
};

const SectionHeading = ({ title, hint }) => (
  <Box sx={{ mb: 2.5 }}>
    <Typography component="h2" sx={{ fontSize: 18, fontWeight: 500, color: C.green, lineHeight: 1.4 }}>{title}</Typography>
    {hint && <Typography sx={{ fontSize: 14, color: C.muted, mt: 0.25 }}>{hint}</Typography>}
  </Box>
);

/** Single-choice chips. Real radio inputs sit under the chips, so arrow keys and form semantics work. */
const ChipGroup = ({ legend, name, value, options, onChange }) => (
  <Box component="fieldset" sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
    <Typography component="legend" sx={{ p: 0, fontSize: 13, fontWeight: 500, letterSpacing: '0.02em', color: C.ink2 }}>
      {legend} <Box component="span" aria-hidden="true">*</Box>
    </Typography>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
      {options.map((option) => (
        <Box key={option} component="label" sx={{ position: 'relative', display: 'inline-flex', cursor: 'pointer' }}>
          <Box
            component="input"
            type="radio"
            name={name}
            value={option}
            checked={value === option}
            onChange={() => onChange(option)}
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', m: 0, opacity: 0, cursor: 'pointer' }}
          />
          <Box
            component="span"
            sx={{
              display: 'inline-flex', alignItems: 'center', height: 38, px: 2, borderRadius: '8px',
              border: `1.5px solid ${C.field}`, bgcolor: C.floor, color: C.green, fontSize: 14.5, fontWeight: 500,
              transition: `background-color 0.2s ${EASE}, border-color 0.2s ${EASE}, color 0.2s ${EASE}`,
              '&:hover': { borderColor: C.green },
              'input:checked + &': { bgcolor: C.green, borderColor: C.green, color: '#FFFFFF', fontWeight: 600 },
              'input:focus-visible + &': FOCUS,
              ...REDUCED_MOTION,
            }}
          >
            {option}
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
);

/** The suggestion as it will appear as a row in the Workstream Suggestions register. */
const IdeaCard = ({ idea }) => {
  const clamp = (lines) => ({ display: '-webkit-box', WebkitLineClamp: lines, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word' });
  const placeholder = (text) => <Box component="span" sx={{ color: '#8A918C' }}>{text}</Box>;
  return (
    <Box sx={{ border: `1px solid ${C.line}`, borderRadius: '12px', bgcolor: C.paper, p: 2.25, boxShadow: SHADOW }}>
      <Typography sx={{ fontSize: 16, fontWeight: 600, lineHeight: 1.4, color: C.green, ...clamp(2) }}>
        {idea.title.trim() || placeholder('Your title appears here')}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.25, minHeight: 28 }}>
        {idea.category && <Chip size="small" label={idea.category} sx={{ borderRadius: '8px', fontWeight: 500, bgcolor: C.floor }} />}
        {idea.impactType && <Chip size="small" variant="outlined" label={idea.impactType} sx={{ borderRadius: '8px', fontWeight: 500 }} />}
      </Box>
      <Typography sx={{ fontSize: 13.5, color: C.muted, mt: 1.25 }}>
        Service <Box component="span" sx={{ color: C.ink, fontWeight: 500 }}>{idea.affectedService.trim() || placeholder('not set')}</Box>
      </Typography>
      <Typography sx={{ fontSize: 14, lineHeight: 1.5, color: C.ink2, mt: 1, ...clamp(3) }}>
        {idea.problem.trim() || placeholder('The problem description appears here.')}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.75, pt: 1.5, borderTop: `1px solid ${C.hair}` }}>
        <Chip size="small" color="info" label="New" sx={{ borderRadius: '8px', fontWeight: 500 }} />
        <Typography sx={{ fontSize: 12.5, color: C.muted }}>Awaiting review</Typography>
      </Box>
    </Box>
  );
};

const CharCount = ({ hint, value, max }) => (
  <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
    <span>{hint}</span>
    {max ? <span aria-live="off">{value.length} / {max}</span> : null}
  </Box>
);

/**
 * Public form (/workstream-ideas/new) to suggest a tech debt, reliability or stability workstream.
 * No sign-in; submissions land in Service Operations > Workstream Suggestions for triage.
 */
const WorkstreamIdeaPage = () => {
  // status: loading | error | open | done
  const [status, setStatus] = useState('loading');
  const [options, setOptions] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [submitted, setSubmitted] = useState(null);
  const [emailTouched, setEmailTouched] = useState(false);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    let cancelled = false;
    getIdeaOptions()
      .then((data) => { if (!cancelled) { setOptions(data); setStatus('open'); } })
      .catch(() => { if (!cancelled) setStatus('error'); });
    return () => { cancelled = true; };
  }, []);

  const set = (name) => (e) => setForm((f) => ({ ...f, [name]: e.target.value }));
  const pick = (name) => (value) => setForm((f) => ({ ...f, [name]: value }));
  const limits = options?.limits || {};

  const emailValid = EMAIL_RE.test(form.submitterEmail.trim());
  const requiredDone = [
    form.title.trim(), form.category, form.affectedService.trim(), form.impactType, form.problem.trim(), emailValid,
  ].filter(Boolean).length;
  const REQUIRED_TOTAL = 6;
  const ready = requiredDone === REQUIRED_TOTAL;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!ready) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      await submitIdea(form);
      setSubmitted(form);
      setStatus('done');
      window.scrollTo(0, 0);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const field = (name, label, props = {}) => (
    <TextField
      id={`idea-${name}`}
      label={label}
      value={form[name]}
      onChange={set(name)}
      fullWidth
      size="small"
      inputProps={TEXT_LIMIT_FIELDS.includes(name) ? { maxLength: limits[name] } : {}}
      {...props}
    />
  );

  return (
    // body has zoom: 0.8 (assets/styles/main.css), so 100vh / 0.8 is the full window.
    <Box sx={{ minHeight: 'calc(100vh / 0.8)', bgcolor: C.floor, color: C.ink }}>
      <Box component="header" sx={{ bgcolor: C.green, color: '#FFFFFF', px: { xs: 2, sm: 6 }, py: { xs: 3, sm: 4 } }}>
        <Box sx={{ maxWidth: 1120, mx: 'auto' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: C.onGreenMuted }}>Service Operations</Typography>
          <Typography component="h1" sx={{ mt: 0.75, fontSize: { xs: 30, sm: 40 }, fontWeight: 300, letterSpacing: '-0.02em', lineHeight: 1.2, color: '#FFFFFF' }}>
            Suggest tech debt, reliability or stability work
          </Typography>
          <Typography sx={{ mt: 1, maxWidth: 620, fontSize: 15, lineHeight: 1.55, color: C.onGreen }}>
            Name the service, the problem and what it costs us. The Service Operations team reviews every suggestion and decides what to plan. No sign-in needed.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1120, mx: 'auto', px: { xs: 2, sm: 6 }, py: { xs: 3, sm: 4 } }}>
        {status === 'loading' && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress sx={{ color: C.green }} /></Box>}
        {status === 'error' && <Alert severity="error">The form could not be loaded. Try again in a moment.</Alert>}

        {status === 'done' && submitted && (
          <Box sx={{ maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box>
              <Typography component="h2" sx={{ fontSize: 24, fontWeight: 400, color: C.green }}>Suggestion registered</Typography>
              <Typography sx={{ mt: 0.5, fontSize: 15, color: C.ink2, lineHeight: 1.55 }}>
                The team will review it. This is how it appears in the register.
              </Typography>
            </Box>
            <IdeaCard idea={submitted} />
            <Box>
              <Button variant="outlined" onClick={() => { setForm(EMPTY); setSubmitted(null); setEmailTouched(false); setEvidenceOpen(false); setStatus('open'); }}
                sx={{ textTransform: 'none', fontWeight: 600, borderWidth: 1.5, borderRadius: '10px', color: C.green, borderColor: C.green }}>
                Make another suggestion
              </Button>
            </Box>
          </Box>
        )}

        {status === 'open' && (
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) 340px' }, gap: { xs: 3, md: 4 }, alignItems: 'start' }}
          >
            <Box sx={{ bgcolor: C.paper, border: `1px solid ${C.hair}`, borderRadius: '12px', boxShadow: SHADOW }}>
              <Box component="section" sx={{ p: { xs: 2.5, sm: 4 } }}>
                <SectionHeading title="What are you suggesting?" />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {field('title', 'Title', { required: true, size: 'medium', InputProps: { sx: { fontSize: 20 } }, helperText: 'One line that sums it up.' })}
                  <ChipGroup legend="Category" name="category" value={form.category} options={options?.categories || []} onChange={pick('category')} />
                </Box>
              </Box>

              <Box component="section" sx={{ p: { xs: 2.5, sm: 4 }, borderTop: `1px solid ${C.hair}` }}>
                <SectionHeading title="What is the problem?" />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {field('problem', 'Problem description', {
                    required: true, multiline: true, minRows: 4,
                    helperText: <CharCount hint="What hurts today, and who or what is affected?" value={form.problem} max={limits.problem} />,
                  })}
                  {field('proposedSolution', 'Proposed solution or outcome', {
                    multiline: true, minRows: 3,
                    helperText: <CharCount hint="Optional. What would done look like?" value={form.proposedSolution} max={limits.proposedSolution} />,
                  })}
                </Box>
              </Box>

              <Box component="section" sx={{ p: { xs: 2.5, sm: 4 }, borderTop: `1px solid ${C.hair}` }}>
                <SectionHeading title="Where does it hurt?" />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {field('affectedService', 'Affected service or component', { required: true, helperText: 'For example: Datadog monitors, Document Service, CI pipeline.' })}
                  <ChipGroup legend="Impact type" name="impactType" value={form.impactType} options={options?.impactTypes || []} onChange={pick('impactType')} />
                </Box>
              </Box>

              <Box component="section" sx={{ p: { xs: 2.5, sm: 4 }, borderTop: `1px solid ${C.hair}` }}>
                <Box
                  component="button"
                  type="button"
                  aria-expanded={evidenceOpen}
                  aria-controls="idea-evidence"
                  onClick={() => setEvidenceOpen((o) => !o)}
                  sx={{
                    display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', gap: 2, p: 0,
                    border: 0, bgcolor: 'transparent', font: 'inherit', textAlign: 'left', cursor: 'pointer', borderRadius: '8px',
                    '&:focus-visible': FOCUS,
                  }}
                >
                  <Box>
                    <Typography component="span" sx={{ display: 'block', fontSize: 18, fontWeight: 500, color: C.green }}>Add evidence</Typography>
                    <Typography component="span" sx={{ display: 'block', fontSize: 14, color: C.muted, mt: 0.25 }}>Optional. Incident links, dashboards and work items help the team prioritise.</Typography>
                  </Box>
                  <Box component="span" aria-hidden="true" sx={{ flexShrink: 0, fontSize: 22, lineHeight: 1, color: C.green, transition: `transform 0.2s ${EASE}`, transform: evidenceOpen ? 'rotate(45deg)' : 'none', ...REDUCED_MOTION }}>+</Box>
                </Box>
                <Collapse in={evidenceOpen} id="idea-evidence">
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 3 }}>
                    {field('evidenceLinks', 'Evidence links', {
                      multiline: true, minRows: 2,
                      helperText: `One full URL per line. Up to ${limits.evidenceLinks || 5}.`,
                    })}
                    {field('adoWorkItems', 'Related ADO work items', { helperText: 'Comma separated, for example AB#123456, AB#123457.' })}
                  </Box>
                </Collapse>
              </Box>

              <Box component="section" sx={{ p: { xs: 2.5, sm: 4 }, borderTop: `1px solid ${C.hair}` }}>
                <SectionHeading title="Who are you?" hint="Your email is used only so the team can follow up with questions about this suggestion." />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                  {field('submitterName', 'Name')}
                  {field('submitterEmail', 'Email', {
                    required: true, type: 'email', onBlur: () => setEmailTouched(true),
                    error: emailTouched && !!form.submitterEmail.trim() && !emailValid,
                    helperText: emailTouched && !!form.submitterEmail.trim() && !emailValid ? 'Enter a valid email address.' : ' ',
                  })}
                </Box>
              </Box>

              {/* Honeypot: hidden from people and assistive tech; bots that fill every input give themselves away. */}
              <Box aria-hidden="true" sx={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
                <label htmlFor="idea-website">Website</label>
                <input id="idea-website" name="website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set('website')} />
              </Box>
            </Box>

            <Box component="aside" aria-label="Register preview" sx={{ position: { md: 'sticky' }, top: { md: 24 }, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 500, letterSpacing: '0.02em', color: C.muted, mb: 1 }}>How it will appear in the register</Typography>
                <IdeaCard idea={form} />
              </Box>

              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>{ready ? 'Ready to submit' : 'Required fields'}</Typography>
                  <Typography aria-live="polite" sx={{ fontSize: 13.5, color: C.muted }}>{requiredDone} of {REQUIRED_TOTAL}</Typography>
                </Box>
                <Box role="progressbar" aria-label="Required fields completed" aria-valuemin={0} aria-valuemax={REQUIRED_TOTAL} aria-valuenow={requiredDone}
                  sx={{ height: 6, borderRadius: '999px', bgcolor: C.line, overflow: 'hidden' }}>
                  <Box sx={{ height: '100%', width: `${(requiredDone / REQUIRED_TOTAL) * 100}%`, borderRadius: '999px', bgcolor: C.orange, transition: `width 0.25s ${EASE}`, ...REDUCED_MOTION }} />
                </Box>
              </Box>

              <Box
                component="button"
                type="submit"
                disabled={submitting || !ready}
                sx={{
                  height: 48, px: 3.5, borderRadius: '10px', border: 'none', font: 'inherit', fontSize: 15.5, fontWeight: 600,
                  bgcolor: C.green, color: '#FFFFFF', cursor: 'pointer', boxShadow: '0 3px 6px rgba(0,0,0,0.12)',
                  transition: `background-color 0.2s ${EASE}, transform 0.2s ${EASE}, box-shadow 0.2s ${EASE}`,
                  '&:hover:not(:disabled)': { bgcolor: C.greenLight, transform: 'translateY(-2px)', boxShadow: '0 8px 10px rgba(0,0,0,0.08), 0 3px 6px rgba(0,0,0,0.06)' },
                  '&:active:not(:disabled)': { bgcolor: C.greenDark, transform: 'none', boxShadow: SHADOW },
                  '&:focus-visible': FOCUS,
                  '&:disabled': { bgcolor: '#DDE3DF', color: '#59625C', cursor: 'not-allowed', boxShadow: 'none' },
                  '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover:not(:disabled)': { transform: 'none' } },
                }}
              >
                {submitting ? 'Submitting…' : 'Submit suggestion'}
              </Box>
              {submitError && <Alert severity="error" sx={{ py: 0 }}>{submitError}</Alert>}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default WorkstreamIdeaPage;
